<template>
  <div class="incentive-report-page">
    <section class="report-hero">
      <div>
        <div class="hero-eyebrow">RIDER REWARD ANALYTICS</div>
        <h1 class="hero-title">Order Incentive Report</h1>
        <p class="hero-subtitle">
          ตรวจสอบสถานะและยอดจ่าย Order Incentive แยกตามจังหวัดและช่วงเวลา
        </p>
      </div>
    </section>

    <v-card class="report-shell glass-card">
      <v-toolbar flat color="transparent" class="report-toolbar">
        <div class="toolbar-copy">
          <div class="toolbar-title">Payout report</div>
          <div class="toolbar-subtitle">
            {{ reportRows.length }} รายการในช่วงเวลาที่เลือก
          </div>
        </div>
        <v-spacer />
        <download-excel
          type="csv"
          :data="exportRows"
          :fields="exportFields"
          :name="exportFileName + '.csv'"
        >
          <v-btn round outline color="blue-grey" :disabled="!reportRows.length">
            <v-icon left small>file_download</v-icon>
            Export CSV
          </v-btn>
        </download-excel>
        <v-btn
          round
          outline
          color="green"
          :disabled="!reportRows.length"
          @click="exportXlsx"
        >
          <v-icon left small>file_download</v-icon>
          Export XLSX
        </v-btn>
        <v-btn
          icon
          color="primary"
          class="black--text"
          :loading="loading"
          @click="loadReport"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="filter-panel">
        <v-layout row wrap align-end>
          <v-flex xs12 sm6 md3 class="px-1">
            <v-menu
              v-model="dateFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template slot="activator" slot-scope="{ on }">
                <v-text-field
                  v-on="on"
                  :value="dateFrom"
                  label="วันที่เริ่ม"
                  prepend-icon="event"
                  outline
                  readonly
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="dateFrom"
                color="primary"
                :max="dateTo || undefined"
                @input="dateFromMenu = false"
              />
            </v-menu>
          </v-flex>
          <v-flex xs12 sm6 md3 class="px-1">
            <v-menu
              v-model="dateToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              full-width
              min-width="290px"
            >
              <template slot="activator" slot-scope="{ on }">
                <v-text-field
                  v-on="on"
                  :value="dateTo"
                  label="วันที่สิ้นสุด"
                  prepend-icon="event_available"
                  outline
                  readonly
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="dateTo"
                color="primary"
                :min="dateFrom || undefined"
                @input="dateToMenu = false"
              />
            </v-menu>
          </v-flex>
          <v-flex xs12 sm6 md2 class="px-1">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="Status"
              prepend-icon="filter_list"
              outline
              hide-details
            />
          </v-flex>
          <v-spacer></v-spacer>
          <v-flex xs12 sm6 md2 class="px-1">
            <v-btn
              round
              block
              color="primary"
              class="black--text"
              :loading="loading"
              @click="loadReport"
            >
              <v-icon left small>search</v-icon>
              ค้นหารายงาน
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-layout row wrap class="summary-row">
        <v-flex
          v-for="item in summaryCards"
          :key="item.label"
          xs12
          sm4
          class="pa-1"
        >
          <div class="summary-item">
            <v-icon :color="item.color">{{ item.icon }}</v-icon>
            <div>
              <div class="summary-value">{{ item.value }}</div>
              <div class="summary-label">{{ item.label }}</div>
            </div>
          </div>
        </v-flex>
      </v-layout>

      <v-data-table
        :headers="headers"
        :items="reportRows"
        :loading="loading"
        class="report-table"
      >
        <template slot="items" slot-scope="{ item }">
          <td>
            <div class="strong-text">{{ item.order_code }}</div>
            <div class="muted-text">ID {{ item.order_id }}</div>
          </td>
          <td>{{ item.province }}</td>
          <td>{{ serviceName(item.order_type) }}</td>
          <td>{{ displayDateTime(item.order_time_start) }}</td>
          <td>
            <div class="strong-text">
              {{
                item.final_rider_name || `Rider ${item.final_rider_id || "-"}`
              }}
            </div>
            <div class="muted-text">ID {{ item.final_rider_id || "-" }}</div>
          </td>
          <td>
            <v-chip small :color="statusColor(item.status)" text-color="white">
              {{ statusLabel(item.status) }}
            </v-chip>
          </td>
          <td class="text-xs-right">
            <span class="amount-text">{{ money(item.amount) }} บาท</span>
          </td>
        </template>
        <template slot="no-data">
          <div class="empty-state">
            <v-icon color="blue-grey lighten-2" size="42">search</v-icon>
            <div class="empty-state__title">ไม่พบข้อมูลในช่วงเวลานี้</div>
            <div class="muted-text">
              ลองเปลี่ยนวันที่หรือจังหวัดแล้วค้นหาอีกครั้ง
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="snackbar" :color="messageColor">
      {{ message }}
      <v-btn flat @click="snackbar = false">ปิด</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { globalAxios } from "@/axios-path.js";
import * as XLSX from "xlsx";

const today = () => new Date().toISOString().slice(0, 10);

export default {
  props: {
    province: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      rows: [],
      totals: [],
      provinceFilter: this.province || "",
      dateFrom: today(),
      dateTo: today(),
      dateFromMenu: false,
      dateToMenu: false,
      statusFilter: "paid",
      statusOptions: [
        { text: "All statuses", value: "" },
        { text: "รอจบงาน", value: "pending" },
        { text: "รออนุมัติ", value: "pending_approval" },
        { text: "กำลังจ่าย", value: "processing" },
        { text: "จ่ายแล้ว", value: "paid" },
        { text: "ยกเลิก", value: "cancelled" },
      ],
      exportFields: {
        No: "no",
        "Order code": "order_code",
        "Order ID": "order_id",
        Province: "province",
        Service: "service",
        "Order date": "order_date",
        Rider: "rider_name",
        "Rider ID": "rider_id",
        Status: "status",
        "Amount (THB)": "amount",
      },
      snackbar: false,
      message: "",
      messageColor: "success",
      headers: [
        { text: "Order", value: "order_code" },
        { text: "Province", value: "province" },
        { text: "Service", value: "order_type" },
        { text: "Order date", value: "order_time_start" },
        { text: "Rider", value: "final_rider_name" },
        { text: "Status", value: "status" },
        { text: "Amount", value: "amount", align: "right" },
      ],
    };
  },
  computed: {
    reportRows() {
      if (!this.statusFilter) return this.rows;
      return this.rows.filter((item) => item.status === this.statusFilter);
    },
    paidRows() {
      return this.reportRows.filter((item) => item.status === "paid");
    },
    exportRows() {
      return this.reportRows.map((item, index) => ({
        no: index + 1,
        order_code: item.order_code || "",
        order_id: item.order_id || "",
        province: item.province || "",
        service: this.serviceName(item.order_type),
        order_date: this.displayDateTime(item.order_time_start),
        rider_name:
          item.final_rider_name || `Rider ${item.final_rider_id || "-"}`,
        rider_id: item.final_rider_id || "",
        status: this.statusLabel(item.status),
        amount: Number(item.amount || 0),
      }));
    },
    exportFileName() {
      return `Order-Incentive-Report_${this.dateFrom}_to_${this.dateTo}_${
        this.statusFilter || "all"
      }`;
    },
    summaryCards() {
      return [
        {
          label: "Report items",
          value: this.reportRows.length,
          icon: "receipt",
          color: "blue-grey",
        },
        {
          label: "Paid transactions",
          value: this.paidRows.length,
          icon: "check_circle",
          color: "success",
        },
        {
          label: "Total paid",
          value: `${this.money(
            this.paidRows.reduce(
              (sum, item) => sum + Number(item.amount || 0),
              0
            )
          )} ฿`,
          icon: "account_balance_wallet",
          color: "warning",
        },
      ];
    },
  },
  created() {
    this.loadReport();
  },
  methods: {
    money(value) {
      return Number(value || 0).toLocaleString("th-TH");
    },
    serviceName(type) {
      return Number(type) === 2 ? "Messenger" : "Food";
    },
    displayDateTime(value) {
      return value ? new Date(value).toLocaleString("th-TH") : "-";
    },
    statusColor(status) {
      return (
        {
          pending: "blue-grey",
          pending_approval: "warning",
          processing: "info",
          paid: "success",
          cancelled: "error",
        }[status] || "grey"
      );
    },
    statusLabel(status) {
      return (
        {
          pending: "รอจบงาน",
          pending_approval: "รออนุมัติ",
          processing: "กำลังจ่าย",
          paid: "จ่ายแล้ว",
          cancelled: "ยกเลิก",
        }[status] || status
      );
    },
    notify(message, color = "success") {
      this.message = message;
      this.messageColor = color;
      this.snackbar = true;
    },
    exportXlsx() {
      if (!this.exportRows.length) return;
      const worksheet = XLSX.utils.json_to_sheet(this.exportRows, {
        header: [
          "no",
          "order_code",
          "order_id",
          "province",
          "service",
          "order_date",
          "rider_name",
          "rider_id",
          "status",
          "amount",
        ],
      });
      XLSX.utils.sheet_add_aoa(
        worksheet,
        [
          [
            "No",
            "Order code",
            "Order ID",
            "Province",
            "Service",
            "Order date",
            "Rider",
            "Rider ID",
            "Status",
            "Amount (THB)",
          ],
        ],
        { origin: "A1" }
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Order Incentives");
      XLSX.writeFile(workbook, `${this.exportFileName}.xlsx`);
    },
    async loadReport() {
      this.loading = true;
      try {
        const params = {
          date_from: this.dateFrom,
          date_to: this.dateTo,
        };
        if (
          String(
            this.provinceFilter || this.$route.params.province || ""
          ).trim()
        ) {
          params.province = String(
            this.provinceFilter || this.$route.params.province
          ).trim();
        }
        const response = await globalAxios.get(
          "/api/v1/order-incentives/report",
          { params }
        );
        this.rows = response.data.rows || [];
        this.totals = response.data.province_totals || [];
      } catch (error) {
        this.notify(
          error.response?.data?.detail || "โหลดรายงานไม่สำเร็จ",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.incentive-report-page {
  min-height: 100%;
  padding: 24px 16px 40px;
  position: relative;
}

.report-hero {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto 22px;
  max-width: 1400px;
  padding: 18px 10px;
  position: relative;
}

.hero-eyebrow {
  color: #9b8500;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
}

.hero-title {
  color: #263238;
  font-size: 42px;
  font-weight: 750;
  letter-spacing: -0.04em;
  line-height: 1.1;
  margin: 7px 0;
  text-shadow: 2px 3px 12px rgba(33, 33, 32, 0.25);
}

.hero-subtitle {
  color: #73808a;
  font-size: 16px;
  margin: 0;
}

.hero-icon {
  align-items: center;
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 22px;
  box-shadow: 0 18px 45px rgba(44, 62, 80, 0.11);
  display: flex;
  height: 82px;
  justify-content: center;
  width: 82px;
}

.hero-icon .v-icon {
  color: #d6b900;
  font-size: 38px;
}

.report-shell {
  border-radius: 16px;
  margin: 0 auto;
  max-width: 1400px;
  overflow: hidden;
  position: relative;
}

.glass-card {
  background: rgba(255, 255, 255, 0.68) !important;
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  box-shadow: 0 18px 52px rgba(27, 39, 51, 0.1) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.report-toolbar {
  min-height: 88px;
  padding: 0 18px;
}

.toolbar-title {
  color: #263238;
  font-size: 20px;
  font-weight: 700;
}

.toolbar-subtitle,
.muted-text,
.summary-label {
  color: #777777;
  font-size: 12px;
}

.filter-panel {
  background: rgba(247, 249, 250, 0.82);
  border-bottom: 1px solid #e3e8eb;
  border-top: 1px solid #e3e8eb;
  padding: 18px;
}

.summary-row {
  padding: 8px 16px 16px;
}

.summary-item {
  align-items: center;
  background: rgba(255, 255, 255, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  box-shadow: 0 8px 22px rgba(35, 45, 55, 0.06);
  display: flex;
  gap: 12px;
  min-height: 74px;
  padding: 14px;
}

.summary-value {
  color: #222222;
  font-size: 22px;
  font-weight: 700;
}

.strong-text {
  color: #222222;
  font-weight: 600;
}

.amount-text {
  color: #1b5e20;
  font-size: 17px;
  font-weight: 750;
}

.report-table {
  border-top: 1px solid #eeeeee;
}

.report-table >>> table {
  min-width: 1080px;
}

.empty-state {
  padding: 34px 16px;
  text-align: center;
}

.empty-state__title {
  color: #263238;
  font-size: 17px;
  font-weight: 700;
  margin: 8px 0 2px;
}

@media (max-width: 600px) {
  .incentive-report-page {
    padding: 12px 6px 28px;
  }

  .hero-title {
    font-size: 30px;
  }

  .hero-icon {
    display: none;
  }
}
</style>

<style>
.incentive-report-page .v-btn {
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.incentive-report-page .v-btn:not(.v-btn--icon) {
  border-radius: 24px;
  min-width: 104px;
  padding-left: 24px;
  padding-right: 24px;
  box-shadow: 0 8px 18px rgba(38, 50, 56, 0.18) !important;
}
</style>
